import { Box, Tooltip, Typography } from '@mui/material';
import { BOX_CAPACITY } from '../config';

export interface OccupiedSlot {
  position: string;
  cell_line_name?: string | null;
  record_id?: string | null;
}

interface BoxGridProps {
  /** Slots currently holding a vial, keyed by numeric position ("1".."100"). */
  occupied?: OccupiedSlot[];
  /** Positions the operator has picked (add/revive flows). */
  selected?: string[];
  /** Positions that clash with an existing vial (shown in the accent colour). */
  conflicts?: string[];
  /** Toggle a slot; when provided the grid is interactive. */
  onToggle?: (position: string) => void;
  capacity?: number;
  /** Columns in the physical grid (10 → a 10×10 box of 100). */
  columns?: number;
}

const CELL = 26;
const GAP = 4;

/**
 * Visual map of a cryopreservation box: filled / empty / selected / conflicting
 * slots. Read-only by default; pass `onToggle` to let the operator pick slots.
 */
export default function BoxGrid({
  occupied = [],
  selected = [],
  conflicts = [],
  onToggle,
  capacity = BOX_CAPACITY,
  columns = 10,
}: BoxGridProps) {
  const occupiedMap = new Map(occupied.map((o) => [String(o.position), o]));
  const selectedSet = new Set(selected.map(String));
  const conflictSet = new Set(conflicts.map(String));
  const interactive = typeof onToggle === 'function';

  const slotStyle = (pos: string) => {
    const isOccupied = occupiedMap.has(pos);
    const isSelected = selectedSet.has(pos);
    const isConflict = conflictSet.has(pos);
    if (isConflict) {
      return { bg: 'var(--color-accent)', fg: 'var(--color-on-accent, #fff)', border: 'var(--color-accent)' };
    }
    if (isSelected) {
      return { bg: 'var(--color-primary)', fg: 'var(--color-on-primary, #fff)', border: 'var(--color-primary)' };
    }
    if (isOccupied) {
      return {
        bg: 'var(--color-primary-container)',
        fg: 'var(--color-on-primary-container)',
        border: 'var(--color-primary-container)',
      };
    }
    return { bg: 'var(--color-surface-2)', fg: 'var(--color-faint)', border: 'var(--color-outline)' };
  };

  return (
    <Box
      role="grid"
      aria-label="Box position map"
      sx={{
        display: 'grid',
        gridTemplateColumns: `repeat(${columns}, ${CELL}px)`,
        gap: `${GAP}px`,
        justifyContent: 'center',
      }}
    >
      {Array.from({ length: capacity }, (_, i) => {
        const pos = String(i + 1);
        const occ = occupiedMap.get(pos);
        const { bg, fg, border } = slotStyle(pos);
        const label = occ
          ? `Position ${pos}: ${occ.cell_line_name || 'occupied'}`
          : selectedSet.has(pos)
            ? `Position ${pos}: selected`
            : `Position ${pos}: empty`;
        const cell = (
          <Box
            role="gridcell"
            aria-label={label}
            aria-disabled={!interactive}
            onClick={interactive ? () => onToggle!(pos) : undefined}
            sx={{
              width: CELL,
              height: CELL,
              borderRadius: '6px',
              bgcolor: bg,
              color: fg,
              border: `1px solid ${border}`,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '0.62rem',
              fontWeight: 600,
              cursor: interactive ? 'pointer' : 'default',
              userSelect: 'none',
              transition: 'transform 0.08s, box-shadow 0.15s',
              '&:hover': interactive ? { transform: 'scale(1.12)', boxShadow: 'var(--shadow-e1)' } : undefined,
            }}
          >
            {pos}
          </Box>
        );
        return (
          <Tooltip key={pos} title={label} arrow disableInteractive>
            {cell}
          </Tooltip>
        );
      })}
    </Box>
  );
}

/** Small colour key so the grid reads at a glance. */
export function BoxGridLegend() {
  const item = (color: string, border: string, text: string) => (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
      <Box sx={{ width: 14, height: 14, borderRadius: '4px', bgcolor: color, border: `1px solid ${border}` }} />
      <Typography variant="caption" sx={{ color: 'var(--color-muted)' }}>{text}</Typography>
    </Box>
  );
  return (
    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, justifyContent: 'center', mt: 1.5 }}>
      {item('var(--color-surface-2)', 'var(--color-outline)', 'Empty')}
      {item('var(--color-primary-container)', 'var(--color-primary-container)', 'Occupied')}
      {item('var(--color-primary)', 'var(--color-primary)', 'Selected')}
      {item('var(--color-accent)', 'var(--color-accent)', 'Conflict')}
    </Box>
  );
}
